import * as React from 'react';
import styled, { css } from 'styled-components';
import { AdvertisingAccountType } from '@amzn/aac-chrome-internal-api';
import { Heading, Text } from '@amzn/storm-ui';
import { useAccountDropdownContext } from './AccountDropdownProvider';
import { useAccountDropdownBundle } from 'src/providers/PantherLocalizationProvider';
import { getCountryCodeByMarketplace } from 'src/commons/utils/advertising-config-store/ACSConfigClient';
import { ACCOUNT_CARD_TYPE } from './AccountTypeLocalizationKeyMapping';
import {
  findSelectedAccountSwitcherAccount,
  getSingleGlobalAccount,
} from './accountHelper';

const InfoCardWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.base.default};
  background-color: ${({ theme }) => theme.color.gray[50]};
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[200]};
`;

const StyledHeading = styled(Heading)`
  text-transform: none;
  overflow-wrap: anywhere;
`;

export const PropertyPairContainer = styled.div`
  line-height: 18px;

  &:not(:last-of-type) {
    margin-bottom: 3px;
  }

  & > *:first-of-type {
    display: inline-block;
  }
`;

export const ColonText = styled(Text)`
  margin-inline-end: 2px;
`;

export const WrappedText = styled(Text)<{ bold?: boolean }>`
  color: ${({ theme }) => theme.color.gray[700]};
  ${({ bold }) =>
    bold &&
    css`
      font-weight: 700;
    `}
  word-break: break-all;
  line-height: 18px;
`;

export const PropertyPair = ({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement => {
  return (
    <PropertyPairContainer>
      <Text size="xsmall" inline color="tertiary">
        {label}
      </Text>
      <ColonText size="xsmall" inline>
        :
      </ColonText>
      <WrappedText size="xsmall" inline>
        {value}
      </WrappedText>
    </PropertyPairContainer>
  );
};

export const IA2AccountDropdownInfoCard: React.VFC = () => {
  const {
    currentAccountName,
    currentAccountType,
    currentMarketplaceId,
    currentEntityId,
    accounts,
    isManagerAPartner,
    retailerName,
  } = useAccountDropdownContext();
  const bundle = useAccountDropdownBundle();
  const singleMarketplaceLabelKey =
    getCountryCodeByMarketplace(currentMarketplaceId);
  const singleMarketplaceLabel = singleMarketplaceLabelKey
    ? bundle.getMessage(
        `account-switcher-marketplace-${singleMarketplaceLabelKey}`,
      )
    : '';
  const selectedAccount = findSelectedAccountSwitcherAccount(
    currentEntityId,
    accounts.map((accountGroup) => accountGroup.accounts).flat(),
  );

  const singleGlobalAccount = getSingleGlobalAccount(accounts, currentEntityId);

  const nestedAccounts = selectedAccount?.nestedAccounts;
  const marketplaceLabel =
    (nestedAccounts && nestedAccounts.length > 1) ||
    currentAccountType === AdvertisingAccountType.DSP_ENTITY
      ? bundle.getMessage('account-switcher-multiple-countries')
      : singleMarketplaceLabel;

  const currentAccountTypeLabel = bundle.getMessage(
    ACCOUNT_CARD_TYPE.get(
      singleGlobalAccount ? AdvertisingAccountType.GLOBAL : currentAccountType,
    ) || '',
  );

  return (
    <InfoCardWrapper>
      <StyledHeading renderAs="h6" typeface="display">
        {selectedAccount?.label || currentAccountName}
      </StyledHeading>

      <WrappedText size="small">{currentAccountTypeLabel}</WrappedText>

      {retailerName && (
        <WrappedText size="small" bold>
          {retailerName}
        </WrappedText>
      )}

      {currentAccountType !== AdvertisingAccountType.MANAGER &&
        currentAccountType !== AdvertisingAccountType.DSP_ENTITY && (
          <WrappedText size="small">{marketplaceLabel}</WrappedText>
        )}

      {!retailerName && (
        <PropertyPair
          label={
            isManagerAPartner
              ? bundle.getMessage('account-dropdown-partner-id-label')
              : bundle.getMessage('account-dropdown-entity-id-label')
          }
          value={selectedAccount?.entityId || currentEntityId}
        />
      )}
    </InfoCardWrapper>
  );
};
